import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";
import type { components } from "@breenova/api-client";

type Schemas = components["schemas"];

export type CurrentUserResponse = Schemas["CurrentUserResponse"];
export type SessionItem = Schemas["GetSessionsSessionItem"];
export type UpdateProfileCommand = Schemas["UpdateProfileCommand"];

export const accountKeys = {
  me: ["account", "me"] as const,
  sessions: ["account", "sessions"] as const,
};

/** Pulls a readable message out of an RFC 9457 problem-details body. */
export function problemMessage(error: unknown, fallback: string): string {
  if (error && typeof error === "object") {
    const problem = error as { detail?: string; title?: string; errors?: Record<string, string[]> };
    const first = problem.errors ? Object.values(problem.errors).flat()[0] : undefined;

    return first ?? problem.detail ?? problem.title ?? fallback;
  }

  return fallback;
}

/**
 * Saves the breeder's own details.
 *
 * The response is the same shape `/auth/me` returns, so the cached profile is replaced
 * with the server's answer rather than with what was typed — the two differ whenever
 * normalisation has had something to say, such as a country code arriving lower case.
 */
export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (command: UpdateProfileCommand): Promise<CurrentUserResponse> => {
      const { data, error } = await api.PUT("/api/v1/auth/me", { body: command });

      if (error || !data) {
        throw new Error(problemMessage(error, "Could not save your details."));
      }

      return data;
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(accountKeys.me, updated);
    },
  });
}

export function useSessions() {
  return useQuery({
    queryKey: accountKeys.sessions,
    queryFn: async (): Promise<SessionItem[]> => {
      const { data, error } = await api.GET("/api/v1/auth/sessions");

      if (error || !data) {
        throw new Error("Could not load your signed-in devices.");
      }

      return data.sessions ?? [];
    },
  });
}

/**
 * Ends one other sign-in, or every one of them when passed no id.
 *
 * The current device is never among them — the server refuses to revoke the session
 * making the request, because the access token in hand would stay valid for its
 * remaining minutes and leave the app looking signed in while every refresh failed.
 */
export function useRevokeSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sessionId?: string): Promise<number> => {
      const { data, error } = sessionId
        ? await api.DELETE("/api/v1/auth/sessions/{id}", {
            params: { path: { id: sessionId } },
          })
        : await api.DELETE("/api/v1/auth/sessions");

      if (error || !data) {
        throw new Error(problemMessage(error, "Could not end that session."));
      }

      return data.sessionsEnded ?? 0;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: accountKeys.sessions });
    },
  });
}

export function useChangePassword() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      currentPassword: string;
      newPassword: string;
    }): Promise<number> => {
      const { data, error } = await api.POST("/api/v1/auth/change-password", { body: input });

      if (error || !data) {
        throw new Error(problemMessage(error, "Could not change your password."));
      }

      return data.sessionsEnded ?? 0;
    },
    onSuccess: () => {
      // Every other device has just been signed out, so the list on screen is stale.
      void queryClient.invalidateQueries({ queryKey: accountKeys.sessions });
    },
  });
}
